<script setup>
import SaveWebsiteLatestMagento1MinimumSupportedVersionAction from "@frontJs/components/actions/projectManagement/dev/SaveWebsiteLatestMagento1MinimumSupportedVersionAction.vue";
import SaveWebsiteLatestMagento1VersionAction from "@frontJs/components/actions/projectManagement/dev/SaveWebsiteLatestMagento1VersionAction.vue";
import SaveWebsiteLatestMagento2MinimumSupportedVersionAction from "@frontJs/components/actions/projectManagement/dev/SaveWebsiteLatestMagento2MinimumSupportedVersionAction.vue";
import SaveWebsiteLatestMagento2VersionAction from "@frontJs/components/actions/projectManagement/dev/SaveWebsiteLatestMagento2VersionAction.vue";
import SaveWebsiteLatestPrestashopMinimumSupportedVersionAction from "@frontJs/components/actions/projectManagement/dev/SaveWebsiteLatestPrestashopMinimumSupportedVersionAction.vue";
import SaveWebsiteLatestPrestashopVersionAction from "@frontJs/components/actions/projectManagement/dev/SaveWebsiteLatestPrestashopVersionAction.vue";
import WebsiteTable from "@frontJs/components/tables/projectManagement/dev/WebsiteTable.vue";
import ProjectManagementLayout from "@frontJs/layouts/ProjectManagementLayout.vue";
import { ArrowPathIcon, PencilIcon } from "@heroicons/vue/24/outline";
import { Head } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import PageTitle from "@js/components/titles/PageTitle.vue";
import LineCardData from "@js/components/cards/LineCardData.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { useRoute } from "@js/utils/route";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { route } = useRoute();

const loading = ref(true);

const data = ref({
  latestMagento1Version: null,
  latestMagento1MinimumSupportedVersion: null,
  latestMagento2Version: null,
  latestMagento2MinimumSupportedVersion: null,
  latestPrestashopVersion: null,
  latestPrestashopMinimumSupportedVersion: null,
});

const selectedTab = ref(0);

function changeTab(index) {
  selectedTab.value = index;
}

function updateData() {
  loading.value = true;

  const request = [
    {
      key: "latest_magento1_version",
    },
    {
      key: "latest_magento1_minimum_supported_version",
    },
    {
      key: "latest_magento2_version",
    },
    {
      key: "latest_magento2_minimum_supported_version",
    },
    {
      key: "latest_prestashop_version",
    },
    {
      key: "latest_prestashop_minimum_supported_version",
    },
  ];

  const promises = request.map((request) => {
    return new Promise((resolve, reject) => {
      axios
        .get(
          route("front.getConfiguration", {
            key: request.key,
          }),
        )
        .then((response) => {
          resolve({ request, response });
        })
        .catch((error) => {
          console.log(error);
          resolve({ request, response: null });
        });
    });
  });

  Promise.all(promises)
    .then((result) => {
      result.forEach((item) => {
        if (item.response) {
          data.value[useChangeCase(item.request.key, "camelCase").value] =
            item.response.data.data.value;
        }
      });
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => {
      loading.value = false;
    });
}

onMounted(() => {
  updateData();
});

defineOptions({ layout: ProjectManagementLayout });
</script>

<template>
  <Head>
    <title>
      {{ t("title.websites") }} - {{ t("title.dev") }} -
      {{ t("title.projectManagement") }} - {{ t("title.datakepler") }}
    </title>
  </Head>

  <PageTitle>
    {{ t("title.projectManagement") }} - {{ t("title.dev") }} -
    {{ t("title.websites") }}
  </PageTitle>

  <LoadingSpinner v-if="loading" />

  <TabGroup
    v-else
    :selected-tab="selectedTab"
    :change-tab="changeTab"
    :tabs="{
      all: t('title.all'),
      magento1: t('title.magento1'),
      magento2: t('title.magento2'),
      prestashop: t('title.prestashop'),
    }"
  >
    <template #panels>
      <TabPanel without-background :unmount="true">
        <WebsiteTable user-session-key="website" />
      </TabPanel>

      <TabPanel without-background :unmount="true">
        <div class="mb-4 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
          <LineCardData :title="t('title.latestVersion')" :icon="ArrowPathIcon">
            {{ data.latestMagento1Version ?? "N/A" }}

            <SaveWebsiteLatestMagento1VersionAction :callback="updateData">
              <template #button="{ setIsOpen }">
                <ActionButton
                  size="small"
                  type="secondary"
                  class="ml-auto size-8"
                  :callback="() => setIsOpen(true)"
                  :title="t('button.edit')"
                  :icon="PencilIcon"
                >
                </ActionButton>
              </template>
            </SaveWebsiteLatestMagento1VersionAction>
          </LineCardData>

          <LineCardData
            :title="t('title.latestMinimumSupportedVersion')"
            :icon="ArrowPathIcon"
          >
            {{ data.latestMagento1MinimumSupportedVersion ?? "N/A" }}

            <SaveWebsiteLatestMagento1MinimumSupportedVersionAction
              :callback="updateData"
            >
              <template #button="{ setIsOpen }">
                <ActionButton
                  size="small"
                  type="secondary"
                  class="ml-auto size-8"
                  :callback="() => setIsOpen(true)"
                  :title="t('button.edit')"
                  :icon="PencilIcon"
                >
                </ActionButton>
              </template>
            </SaveWebsiteLatestMagento1MinimumSupportedVersionAction>
          </LineCardData>
        </div>

        <WebsiteTable user-session-key="website_magento1" cms="magento1" />
      </TabPanel>

      <TabPanel without-background :unmount="true">
        <div class="mb-4 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
          <LineCardData :title="t('title.latestVersion')" :icon="ArrowPathIcon">
            {{ data.latestMagento2Version ?? "N/A" }}

            <SaveWebsiteLatestMagento2VersionAction :callback="updateData">
              <template #button="{ setIsOpen }">
                <ActionButton
                  size="small"
                  type="secondary"
                  class="ml-auto size-8"
                  :callback="() => setIsOpen(true)"
                  :title="t('button.edit')"
                  :icon="PencilIcon"
                >
                </ActionButton>
              </template>
            </SaveWebsiteLatestMagento2VersionAction>
          </LineCardData>

          <LineCardData
            :title="t('title.latestMinimumSupportedVersion')"
            :icon="ArrowPathIcon"
          >
            {{ data.latestMagento2MinimumSupportedVersion ?? "N/A" }}

            <SaveWebsiteLatestMagento2MinimumSupportedVersionAction
              :callback="updateData"
            >
              <template #button="{ setIsOpen }">
                <ActionButton
                  size="small"
                  type="secondary"
                  class="ml-auto size-8"
                  :callback="() => setIsOpen(true)"
                  :title="t('button.edit')"
                  :icon="PencilIcon"
                >
                </ActionButton>
              </template>
            </SaveWebsiteLatestMagento2MinimumSupportedVersionAction>
          </LineCardData>
        </div>

        <WebsiteTable user-session-key="website-magento2" cms="magento2" />
      </TabPanel>

      <TabPanel without-background :unmount="true">
        <div class="mb-4 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
          <LineCardData :title="t('title.latestVersion')" :icon="ArrowPathIcon">
            {{ data.latestPrestashopVersion ?? "N/A" }}

            <SaveWebsiteLatestPrestashopVersionAction :callback="updateData">
              <template #button="{ setIsOpen }">
                <ActionButton
                  size="small"
                  type="secondary"
                  class="ml-auto size-8"
                  :callback="() => setIsOpen(true)"
                  :title="t('button.edit')"
                  :icon="PencilIcon"
                >
                </ActionButton>
              </template>
            </SaveWebsiteLatestPrestashopVersionAction>
          </LineCardData>

          <LineCardData
            :title="t('title.latestMinimumSupportedVersion')"
            :icon="ArrowPathIcon"
          >
            {{ data.latestPrestashopMinimumSupportedVersion ?? "N/A" }}

            <SaveWebsiteLatestPrestashopMinimumSupportedVersionAction
              :callback="updateData"
            >
              <template #button="{ setIsOpen }">
                <ActionButton
                  size="small"
                  type="secondary"
                  class="ml-auto size-8"
                  :callback="() => setIsOpen(true)"
                  :title="t('button.edit')"
                  :icon="PencilIcon"
                >
                </ActionButton>
              </template>
            </SaveWebsiteLatestPrestashopMinimumSupportedVersionAction>
          </LineCardData>
        </div>

        <WebsiteTable user-session-key="website_prestashop" cms="prestashop" />
      </TabPanel>
    </template>
  </TabGroup>
</template>
